import { Injectable, Injector, signal } from '@angular/core';
import { filter, firstValueFrom, take } from 'rxjs';
import usertour from 'usertour.js';
import {AccountService, LawFirmDefinition, UserWithSeat} from 'src/api';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth.service';

@Injectable({ providedIn: 'root' })
export class UserSeatService {
  hasSeat = signal<boolean | undefined>(undefined);
  role = signal<UserWithSeat.RoleEnum | undefined>(undefined);
  isUserAdminOfLawFirm = signal<boolean>(false);
  user = signal<UserWithSeat | undefined>(undefined);
  lawFirm = signal<LawFirmDefinition | undefined>(undefined);
  private usertourInitialized = false;

  constructor(private injector: Injector) {
    const auth = this.injector.get(AuthService);
    auth.ready$.pipe(
      filter(ready => ready && !!auth.keycloak?.token),
      take(1),
    ).subscribe(() => {
      void this.checkUserSeatOnce();
    });
  }

  private async checkUserSeatOnce() {
    try {
      const accountService = this.injector.get(AccountService);
      const [user, lawFirm] = await Promise.all([
        firstValueFrom(accountService.getCurrentUser()),
        firstValueFrom(accountService.getLawFirmInfo()),
      ]);

      this.hasSeat.set(
        (user?.billing_price_id !== null || lawFirm.trial_phase_ends_on !== null) &&
        lawFirm.billing_subscription_id !== null
      );
      this.role.set(user?.role);
      this.isUserAdminOfLawFirm.set(user?.role === UserWithSeat.RoleEnum.Admin);
      this.user.set(user);
      this.lawFirm.set(lawFirm);
      this.identifyUsertourUser(user);
      void this.triggerOnboardingVideo(user);
    } catch (error) {
      console.error('Error checking user seat', error);
      this.hasSeat.set(false);
      this.role.set(undefined);
      this.isUserAdminOfLawFirm.set(false);
      this.user.set(undefined);
      this.lawFirm.set(undefined);
    }
  }

  /**
   * Refresh user seat and law firm data from the API
   */
  async refresh(): Promise<void> {
    await this.checkUserSeatOnce();
  }

  private async triggerOnboardingVideo(user: UserWithSeat): Promise<void> {
    try {
      const { OnboardingVideoService } = await import('./onboarding-video.service');
      const onboardingVideo = this.injector.get(OnboardingVideoService);
      if (user.meta?.['show_onboarding_video'] === true) {
        onboardingVideo.warmupVideo();
      }
      onboardingVideo.tryShowIfNeeded(user);
    } catch (error) {
      console.error('Failed to trigger onboarding video', error);
    }
  }

  private identifyUsertourUser(user: UserWithSeat): void {
    if (!environment.USERTOUR_TOKEN || !user.id) {
      return;
    }

    const auth = this.injector.get(AuthService);
    const nameFromProfile = [user.first_name, user.last_name].filter(Boolean).join(' ');
    const name = nameFromProfile || auth.keycloak?.idTokenParsed?.['name'];

    // Load Usertour only once logged in: a load started before the login redirect gets aborted,
    // which Firefox and Safari report as "Could not load Usertour.js".
    if (!this.usertourInitialized) {
      usertour.init(environment.USERTOUR_TOKEN);
      this.usertourInitialized = true;
    }
    usertour.identify(user.id, {
      ...(name ? { name } : {}),
      ...(user.mail ? { email: user.mail } : {}),
      ...(user.signed_up_at ? { signed_up_at: user.signed_up_at } : {}),
    });
  }
}
